import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { authedHttpFetch, sseStream } from '#pplx/frontend/fetcher/browser.js';
import createOpenApiClient from 'openapi-fetch';
// TODO: why does this never show as exported when it's in fetcher/constants?
const REASON_HEADER = 'X-Perplexity-Request-Reason';
const getNormalizedPath = (urlTemplate, options) => options?.normalizedPath || String(urlTemplate);
/**
 * Create a new instance of the typed rest async client
 * @private Test only
 * @returns
 */
export const _getTypedRestAsyncClient = (createAskRestAsyncClient, config) => {
    const client = createAskRestAsyncClient({
        baseUrl: `https://${safeRandomUUID()}.com`, // We will throw this away for whatever restAsyncServerFetch uses
        fetch: async (request) => {
            const url = new URL(request.url);
            const path = url.pathname;
            const searchParams = url.searchParams;
            const contentType = request.headers.get('content-type') ?? '';
            const isMultipart = contentType.includes('multipart/form-data');
            // Dispatch bodyless requests before yielding to synchronous app startup work.
            const body = isMultipart
                ? await request.formData()
                : request.body === null
                    ? undefined
                    : (await request.text()) || undefined;
            if (isMultipart) {
                request.headers.delete('content-type');
            }
            const reason = request.headers.get(REASON_HEADER) ?? 'typedRestAsyncClient'; // TODO(klym)
            return authedHttpFetch({
                urlPath: searchParams.size > 0 ? `${path}?${searchParams.toString()}` : path,
                method: request.method,
                body,
                headers: request.headers,
                signal: request.signal,
                timeoutMs: request.timeoutMs,
                numRetries: request.numRetries,
                backOffTime: request.backOffTime,
                reason,
                normalizedPath: request.normalizedPath ?? path,
                baseUrlOverride: request.baseUrlOverride ?? config?.baseUrl,
                includeCredentials: request.includeCredentials,
                keepalive: request.keepalive,
                shouldNotAddSourceVersionQueryParams: request.shouldNotAddSourceVersionQueryParams,
                redirect: request.redirect,
                appDomain: config?.appDomain,
            });
        },
    });
    // @ts-expect-error we are adding headers to the options
    return {
        GET: ((urlTemplate, reason, options) => {
            // @ts-expect-error we are adding headers to the options
            return client.GET(urlTemplate, {
                ...(options || {}),
                normalizedPath: getNormalizedPath(urlTemplate, options),
                headers: {
                    ...(options?.headers || {}),
                    [REASON_HEADER]: reason,
                },
            });
        }),
        POST: ((urlTemplate, reason, options) => {
            // @ts-expect-error we are adding headers to the options
            return client.POST(urlTemplate, {
                ...(options || {}),
                normalizedPath: getNormalizedPath(urlTemplate, options),
                headers: {
                    ...(options?.headers || {}),
                    [REASON_HEADER]: reason,
                },
            });
        }),
        PUT: ((urlTemplate, reason, options) => {
            // @ts-expect-error we are adding headers to the options
            return client.PUT(urlTemplate, {
                ...(options || {}),
                normalizedPath: getNormalizedPath(urlTemplate, options),
                headers: {
                    ...(options?.headers || {}),
                    [REASON_HEADER]: reason,
                },
            });
        }),
        PATCH: ((urlTemplate, reason, options) => {
            // @ts-expect-error we are adding headers to the options
            return client.PATCH(urlTemplate, {
                ...(options || {}),
                normalizedPath: getNormalizedPath(urlTemplate, options),
                headers: {
                    ...(options?.headers || {}),
                    [REASON_HEADER]: reason,
                },
            });
        }),
        DELETE: ((urlTemplate, reason, options) => {
            // @ts-expect-error we are adding headers to the options
            return client.DELETE(urlTemplate, {
                ...(options || {}),
                normalizedPath: getNormalizedPath(urlTemplate, options),
                headers: {
                    ...(options?.headers || {}),
                    [REASON_HEADER]: reason,
                },
            });
        }),
        SSE: sse,
    };
};
/**
 * Pre-wired factory that creates a typed REST client using openapi-fetch.
 * Used by generated TypeSpec client packages so they don't import openapi-fetch directly.
 */
export const createTypedClient = (config) => _getTypedRestAsyncClient(createOpenApiClient, config);
export function sse(url, reason, { path, ...options }) {
    return sseStream({
        url: Object.entries(path ?? {}).reduce((acc, [key, value]) => {
            return acc.replace(`{${key}}`, String(value));
        }, url),
        normalizedPath: url,
        ...(options || {}),
        headers: {
            ...(options?.headers || {}),
            [REASON_HEADER]: reason,
        },
    });
}
//# sourceMappingURL=client.js.map